import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { type Dispatch, type SetStateAction, useEffect } from 'react';

import type {
  AssetIntegrationCreateDTO,
  AssetIntegrationDTO,
  AssetIntegrationUpdateDTO
} from '@/features/AdminAssetDataProviderIntegration/AssetDataProviderIntegration.types';
import useHandleError from '@/hooks/useHandleError';
import { ASSET_DATA_PROVIDERS } from '@/pages/Assets/Assets.constants';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  axiosClient,
  getResponseErrorMessages
} from '@/services/BaseApi.service';

export const assetIntegrationsAtom = atom<AssetIntegrationDTO[]>([]);
export const assetIntegrationsDialogModeAtom = atom<'create' | 'edit' | null>(
  null
);
export const assetIntegrationsDialogOpenAtom = atom<boolean>(false);
export const selectedAssetIntegrationAtom = atom<AssetIntegrationDTO>();

export const useFetchAssetIntegrations = () => {
  const [, setAssetIntegrations] = useAtom(assetIntegrationsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const { data, error, isError, isPending, isSuccess } = useQuery({
    queryKey: [`organizations/${selectedOrganization.label}/asset_integrations`]
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch asset integrations.');
    }
    if (isSuccess) {
      const processedData = (data as AssetIntegrationDTO[]).map(
        (integration) =>
          integration.provider === 'shield'
            ? { ...integration, provider: 'vmpe' }
            : integration
      );

      const sortedAssetIntegrations = sortBy(processedData, [
        (integration) =>
          integration.provider === ASSET_DATA_PROVIDERS.GOOGLE_WORKSPACE
            ? 1
            : 0,
        'id'
      ]);
      setAssetIntegrations(sortedAssetIntegrations as AssetIntegrationDTO[]);
    }
  }, [data, isError, isSuccess]);

  return {
    isPending
  };
};

export const useCreateAssetIntegration = (
  handleClose: Dispatch<SetStateAction<boolean>>
) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: (assetIntegration: AssetIntegrationCreateDTO) => {
      // if provider is vmpe, change to shield for backend
      const integrationToSend =
        assetIntegration.provider === 'vmpe'
          ? { ...assetIntegration, provider: 'shield' }
          : assetIntegration;

      return axiosClient.post(
        `organizations/${selectedOrganization.label}/asset_integrations`,
        integrationToSend
      );
    },
    onError: (error: AxiosError) => {
      handleError(
        error,
        getResponseErrorMessages(error) ||
          'Failure to create asset integration.'
      );
    },
    onSuccess: () => {
      enqueueSnackbar(`Asset Integration Created`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organizations/${selectedOrganization.label}/asset_integrations`
        ]
      });
      handleClose(false);
    }
  });

  return {
    createAssetIntegration: mutation.mutate,
    isLoadingCreateAssetIntegration: mutation.isPending
  };
};

export const useUpdateAssetIntegration = (
  handleClose: Dispatch<SetStateAction<boolean>>
) => {
  const [selectedAssetIntegration] = useAtom(selectedAssetIntegrationAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: (assetIntegration: AssetIntegrationUpdateDTO) => {
      return axiosClient.put(
        `organizations/${selectedOrganization.label}/asset_integrations/${selectedAssetIntegration?.id}`,
        assetIntegration
      );
    },
    onError: (error: AxiosError) => {
      handleError(
        error,
        getResponseErrorMessages(error) ||
          'Failure to update asset integration.'
      );
    },
    onSuccess: () => {
      enqueueSnackbar(`Asset Integration Updated`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organizations/${selectedOrganization.label}/asset_integrations`
        ]
      });
      handleClose(false);
    }
  });

  return {
    updateAssetIntegration: mutation.mutate,
    isLoadingUpdateAssetIntegration: mutation.isPending
  };
};

export const useDeleteAssetIntegration = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (integrationId: number) => {
      return axiosClient.delete(
        `organizations/${selectedOrganization.label}/asset_integrations/${integrationId}`
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete asset integration.');
    },
    onSuccess: () => {
      enqueueSnackbar(`Asset Integration Deleted`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organizations/${selectedOrganization.label}/asset_integrations`
        ]
      });
    }
  });
};
